import { useState, useEffect, useCallback } from "react";
import { GoldenSourceHelper } from "../../../app/Switch_Core_React_Standards";
import { fetchAttributeDefinitions } from "../declr_attribute_definition/declrAttributeDefinitionInteractionService";
import { fetchTaxonomyHeaders, fetchTaxonomyDetailsByHeader } from "../components/Workflow-esg/services/EsgTaxonomySyncService";
import {
  fetchAttributeMappings,
  addAttributeMapping,
  removeAttributeMapping,
  fetchAggregationMethods,
} from "./declrAttributeMappingInteractionService";

const emptyForm = {
  AttributeDefinitionId: "",
  TaxonomyDetailRowId: "",
  UnitConversionFactor: 1,
  AggregationMethodCode: "",
  WeightAttributeDefinitionId: "",
};

/**
 * 🎯 ATTRIBUTE MAPPING CONTROLLER (Group ESG admin)
 * Maps a local Attribute Definition to a group taxonomy concept, with a
 * unit conversion factor and a fixed aggregation method — this is the
 * config that Local Declaration values roll up through to become a
 * group-level declared value.
 */
export default function DeclrAttributeMappingController(siteKey) {
  const [definitions, setDefinitions] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [selectedHeaderId, setSelectedHeaderId] = useState("");
  const [concepts, setConcepts] = useState([]);
  const [aggregationMethods, setAggregationMethods] = useState([]);
  const [mappings, setMappings] = useState([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [defs, headerRows, methods, mappingRows] = await Promise.all([
        fetchAttributeDefinitions(null, { siteKey }),
        fetchTaxonomyHeaders({ siteKey }),
        fetchAggregationMethods({ siteKey }),
        fetchAttributeMappings({}, { siteKey }),
      ]);
      setDefinitions(defs || []);
      setHeaders(headerRows || []);
      setAggregationMethods(methods || []);
      setMappings(mappingRows || []);
    } finally {
      setLoading(false);
    }
  }, [siteKey]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!selectedHeaderId) {
      setConcepts([]);
      return;
    }
    fetchTaxonomyDetailsByHeader(selectedHeaderId, { siteKey }).then((rows) => setConcepts(rows || []));
  }, [selectedHeaderId, siteKey]);

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const selectedMethod = aggregationMethods.find(
    (m) => GoldenSourceHelper.get(m, "MethodCode") === form.AggregationMethodCode
  );
  const requiresWeight = !!(selectedMethod && GoldenSourceHelper.get(selectedMethod, "RequiresWeightAttribute"));

  const handleSave = async () => {
    if (!form.AttributeDefinitionId || !form.TaxonomyDetailRowId || !form.AggregationMethodCode) return;
    setSaving(true);
    setError("");
    try {
      await addAttributeMapping(
        {
          AttributeDefinitionId: form.AttributeDefinitionId,
          TaxonomyDetailRowId: form.TaxonomyDetailRowId,
          UnitConversionFactor: Number(form.UnitConversionFactor) || 1,
          AggregationMethodCode: form.AggregationMethodCode,
          WeightAttributeDefinitionId: requiresWeight ? form.WeightAttributeDefinitionId || null : null,
        },
        { siteKey }
      );
      setForm(emptyForm);
      await load();
    } catch (err) {
      setError(err.message || "Could not save mapping.");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (attributeMappingId) => {
    setError("");
    try {
      await removeAttributeMapping(attributeMappingId, { siteKey });
      await load();
    } catch (err) {
      setError(err.message || "Could not remove mapping.");
    }
  };

  return {
    definitions,
    headers,
    selectedHeaderId,
    setSelectedHeaderId,
    concepts,
    aggregationMethods,
    requiresWeight,
    mappings,
    loading,
    saving,
    error,
    form,
    handleFieldChange,
    handleSave,
    handleRemove,
  };
}
